import ArrowLeftLong from '@ixigo/icons/long-arrow-left';
import {
	Accordion,
	AccordionHeader,
	AccordionItem,
	AccordionPanel,
	AccordionTriggerPreset,
} from '@ixigo/iui/accordion';
import { goBackToPrevPath } from '@repo/shared/navigation';
import { useRouter } from '@tanstack/react-router';
import { Fragment, useEffect, useRef } from 'react';
import {
	COMPARISON_CONTENT,
	COMPARISON_FAQ,
	type ComparisonBlock,
} from '../content/comparison-content';
import { APPLY_CONTENT } from '../content/content';
import { IxigoLogo } from '../sections/ixigo-logo';
import { useFluidZoom } from '../sections/use-fluid-zoom';

// /travel-credit-card/comparison — long-form SEO content page (Figma
// "Desktop - 19"). Fully static and SSR'd, dark theme, normal document flow
// (no canvas scaling, no animation libraries).

// List items bold their lead phrase up to the first " — " (matches the design).
function ListItem({ text }: { text: string }) {
	const sep = ' — ';
	const i = text.indexOf(sep);
	return (
		<li className="leading-relaxed">
			{i > 0 ? (
				<>
					<span className="font-semibold text-white">{text.slice(0, i)}</span>
					{sep}
					{text.slice(i + sep.length)}
				</>
			) : (
				text
			)}
		</li>
	);
}

function Block({ block }: { block: ComparisonBlock }) {
	switch (block.type) {
		case 'p':
			return <p className="leading-relaxed text-white/75">{block.text}</p>;
		case 'h3':
			return (
				<h3 className="pt-2 text-sm font-bold tracking-wide text-white">
					{block.text}
				</h3>
			);
		case 'list':
			return (
				<ul className="list-disc space-y-3 pl-5 text-white/75 marker:text-white/40">
					{block.items.map((item) => (
						<ListItem key={item} text={item} />
					))}
				</ul>
			);
		case 'note':
			return (
				<p className="text-sm italic leading-relaxed text-white/55">
					{block.text}
				</p>
			);
		case 'callout':
			return (
				<div className="rounded-xl bg-white/[0.07] px-6 py-5">
					<p className="text-sm font-bold  tracking-wide text-white">
						{block.title}
					</p>
					<p className="mt-2 text-sm leading-relaxed text-white/70">
						{block.text}
					</p>
				</div>
			);
		case 'table': {
			// Column count drives even cell widths + the group-header colSpan, so
			// the same renderer handles the 3-col compare/fee tables AND the 4-col
			// earning table. Prefer the header count; fall back to the first row.
			const colCount =
				block.columns.length || block.groups[0]?.rows[0]?.length || 3;
			const colW = `${(100 / colCount).toFixed(4)}%`;
			return (
				<div>
					{block.caption ? (
						<p className="mb-3 text-sm font-bold tracking-wide text-white">
							{block.caption}
						</p>
					) : null}
					{/* table-fixed: LOCKS columns to the declared % widths (colW) —
					    without it (table-layout:auto, the default), a table ignores
					    its own width and grows to fit content's natural min-content
					    size, which is what pushed this table past the viewport on
					    mobile. Fixed layout instead makes oversized content WRAP
					    inside its column (break-words on every cell below) — no
					    scroll container, no page-level overflow. */}
					<table className="w-full table-fixed border-collapse text-left text-sm">
						{block.columns.length > 0 ? (
							<thead>
								<tr>
									{block.columns.map((col) => (
										<th
											key={col}
											className="break-words px-4 py-3 font-medium text-white/90"
											style={{ width: colW }}
										>
											{col}
										</th>
									))}
								</tr>
							</thead>
						) : null}
						<tbody>
							{block.groups.map((group) => {
								const firstCell = group.rows[0]?.[0];
								const groupKey =
									group.title ??
									(typeof firstCell === 'string' ? firstCell : firstCell?.text);
								return (
									<Fragment key={groupKey}>
										{group.title ? (
											<tr>
												<th
													colSpan={colCount}
													className="break-words px-4 pb-3 pt-8 text-left text-xs font-bold  tracking-wide text-white"
												>
													{group.title}
												</th>
											</tr>
										) : null}
										{group.rows.map((row, i) => {
											// First cell is always the plain-text row label; use as key.
											const rowKey =
												typeof row[0] === 'string' ? row[0] : row[0]?.text;
											return (
												<tr
													key={rowKey}
													className={
														i % 2 === 1 ? 'bg-white/[0.06]' : undefined
													}
												>
													{row.map((cell, j) => (
														<td
															key={`${rowKey}-${j}`}
															className="break-words px-4 py-3 align-top text-white/80"
															style={{ width: colW }}
														>
															{typeof cell === 'string' ? (
																cell
															) : (
																<a
																	href={cell.href}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="text-ixigo-credit underline underline-offset-2 transition-colors hover:text-ixigo-credit-hover"
																>
																	{cell.text}
																</a>
															)}
														</td>
													))}
												</tr>
											);
										})}
									</Fragment>
								);
							})}
						</tbody>
					</table>
				</div>
			);
		}
		default:
			return null;
	}
}

export function ComparisonPage() {
	const router = useRouter();
	// Width-driven zoom matching the hero canvas — DESKTOP ONLY (md≥768): the page
	// is responsive (md: breakpoints), so the 1440-web curve must not shrink the
	// mobile layout. Applied to the INNER content wrapper, not <main>, so the
	// full-bleed dark bg + min-h-dvh fill are unaffected. See useFluidZoom.
	const zoomRef = useRef<HTMLDivElement>(null);
	useFluidZoom(zoomRef, { minWidth: 768 });

	// "Back to home". This page is reached via plain <a href> links from the
	// landing journey (hard navigations — see card-sequence / showcase / deal-
	// scene), so TanStack's router.history has NO record of the prior page and
	// router.history.back() does nothing.
	//
	// `goBackToPrevPath()` — NOT `window.history.back()`, and NOT gated on
	// `window.history.length`. Both were wrong in the PWA, and in the same way:
	//
	//  - `back()` rewinds exactly ONE entry, but entries can SHARE this pathname.
	//    Every "Learn More" is a deep link into this same page with a different
	//    #hash, so the stack accumulates neighbouring /comparison entries (that's
	//    what patchHistory's `aidx` counts). One step then lands on this page
	//    again and the tap looks dead — press twice and you finally leave, which
	//    is the reported symptom.
	//  - `history.length > 1` cannot see that. It also over-reports inside a
	//    webview: it counts entries the host app created before this page loaded
	//    and never decreases, so the "no entry behind us" fallback never fired
	//    where it was needed.
	//
	// `goBackToPrevPath` walks back to the previous DISTINCT pathname
	// (`history.go(-aidx)`), so it leaves the page in one tap however many hash
	// entries are stacked, and reports false only when there is genuinely nothing
	// behind us. Same helper (via backOrExit) the CBCC back button uses.
	const handleBackToHome = () => {
		// Pop to the previous DISTINCT pathname (see above): one tap however many
		// /comparison#hash entries are stacked. This is also the only route home
		// that KEEPS the landing page's existing document — the navigate() below
		// renders the landing route inside THIS document instead, re-running its
		// loader and rebuilding the whole hero journey (GSAP + clips) from
		// scratch, which is what reads as "the page reloaded".
		if (goBackToPrevPath()) return;
		// Direct or crawled load with no history behind us. REPLACE, not push: a
		// push would leave this page in the stack as a neighbour of wherever we
		// land, recreating the same-path pile-up the helper above has to undo.
		router.navigate({ to: '/travel-credit-card', replace: true });
	};

	// Hash deep-link scroll (the home "Learn More" links land here with a
	// #section-id). We do this in an effect rather than rely on the browser's
	// native fragment scroll because useFluidZoom applies `zoom` in a LAYOUT
	// effect on mount — that changes every section's offset AFTER the native
	// scroll already fired, leaving the target mis-positioned. Running here (a
	// passive effect, after layout effects commit) means the zoom is already in
	// place, so scrollIntoView lands accurately. Guarded to client + a real
	// element id; smooth on the client for a polished jump.
	useEffect(() => {
		const id = window.location.hash.slice(1);
		if (!id) return;
		// rAF: ensure the zoom layout effect + first paint have committed before
		// we measure/scroll (offsets are zoom-dependent).
		const raf = requestAnimationFrame(() => {
			const el = document.getElementById(id);
			el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		});
		return () => cancelAnimationFrame(raf);
	}, []);

	return (
		<main className="min-h-dvh w-full bg-[#0B0B0B] text-white">
			<div ref={zoomRef} className="mx-auto max-w-[1280px] px-6 md:px-10">
				{/* Sticky header: logo (left) + "Back to home" control (right) — same
				    glass back-button as the /apply page (icon + label, history-back so
				    the transition slides right). This page has no CVP loader (it's a
				    static/crawlable SEO page), so it shows a way back rather than an
				    apply CTA (which would need the loader and throw here). */}
				<header className="sticky top-0 z-50 flex items-center justify-between gap-4 bg-[#0B0B0B] py-5">
					<IxigoLogo imgClassName="h-8 w-auto object-contain" />
					<button
						type="button"
						onClick={handleBackToHome}
						className="flex items-center gap-2 rounded-xl bg-white/10 px-5 py-3 text-sm font-medium backdrop-blur transition-colors hover:bg-white/20"
					>
						<ArrowLeftLong aria-hidden className="size-4" />
						{APPLY_CONTENT.backToHome}
					</button>
				</header>

				<div className="flex flex-col gap-20 pb-24">
					{COMPARISON_CONTENT.map((section) => (
						<article
							key={section.id}
							id={section.id}
							// scroll-mt clears the sticky header for #section deep-links
							className="flex scroll-mt-24 flex-col gap-5"
						>
							<h2 className="text-2xl font-bold tracking-wide md:text-[28px]">
								{section.title}
							</h2>
							{section.blocks.map((block, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: static content, order never changes
								<Block key={i} block={block} />
							))}
						</article>
					))}

					{/* FAQ accordion — appended at the very end (single-open, iui chevron).
					    Answers are trusted static HTML (lists/links/bold) authored in
					    comparison-content.ts, rendered via dangerouslySetInnerHTML. */}
					<section id="faqs" className="flex scroll-mt-24 flex-col gap-5">
						<h2 className="text-2xl font-bold tracking-wide md:text-[28px]">
							Frequently Asked Questions
						</h2>
						<Accordion multiple={false} className="flex flex-col gap-3">
							{COMPARISON_FAQ.map((faq) => (
								<AccordionItem
									key={faq.question}
									value={faq.question}
									className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]"
								>
									<AccordionHeader>
										<AccordionTriggerPreset className="px-5 py-4 text-left text-sm font-semibold text-white hover:!bg-transparent active:!bg-transparent md:text-base">
											{faq.question}
										</AccordionTriggerPreset>
									</AccordionHeader>
									<AccordionPanel className="px-5 pb-5">
										<div
											className="text-sm leading-relaxed text-white/70 [&_a]:underline [&_li]:my-1"
											// biome-ignore lint/security/noDangerouslySetInnerHtml: trusted static FAQ HTML (no user input)
											dangerouslySetInnerHTML={{ __html: faq.answer }}
										/>
									</AccordionPanel>
								</AccordionItem>
							))}
						</Accordion>
					</section>
				</div>
			</div>
		</main>
	);
}
